import React from "react";
import { euro } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";
import { comparisonAvailable, finiteAmount, isNoSavingsProblem, simSavings } from "@/components/gp/comparison";

const eur = (n) => (finiteAmount(n) ? euro(n) : "—");
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "");
const pct = (n) => finiteAmount(n) ? ` (${Math.abs(Number(n)).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%)` : "";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium text-right" data-testid={testid}>{value ?? "—"}</span></div>
);

function visibleProblems(sim) {
  const canShowOutcome = comparisonAvailable(sim) && Number(sim.confronto.risparmio) <= 0 && sim.mostra_perdita !== false;
  const list = (sim.problemi || []).filter((problem) => canShowOutcome || !isNoSavingsProblem(problem)).map((problem) => typeof problem === "string" ? problem : problem?.messaggio).filter(Boolean);
  if (sim.error) list.unshift(sim.error);
  return [...new Set(list)];
}

function Problems({ sim, prefix }) {
  const list = visibleProblems(sim);
  if (!list.length) return null;
  return (
    <ul className="space-y-1" data-testid={`${prefix}-problems`}>
      {list.map((m) => <li key={m} className="text-xs text-amber-500 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {m}</li>)}
    </ul>
  );
}

export function BillAnalysis({ sim, commodity }) {
  const prefix = `gp-analysis-${commodity.toLowerCase()}`;
  if (!sim) return null;
  const l = sim.lettura || {};
  const c = sim.confronto || {};
  const unit = commodity === "GAS" ? "Smc" : "kWh";
  const comparable = comparisonAvailable(sim);
  const saving = comparable ? Number(c.risparmio) : null;
  const showCompare = comparable && (saving > 0 || sim.mostra_perdita !== false);
  const savings = simSavings(sim);
  const alternatives = comparable ? (sim.alternative || []).filter((a) => savings[a.codice || a.offerta?.codice] != null) : [];
  const showNote = sim.nota && !visibleProblems(sim).includes(sim.nota) && (!isNoSavingsProblem(sim.nota) || (comparable && saving <= 0 && sim.mostra_perdita !== false));
  const period = l.periodoDal ? `nel periodo ${fmtD(l.periodoDal)} – ${fmtD(l.periodoAl)}` : "nel periodo della bolletta";
  const factor = l.consumoPeriodo > 0 && l.consumoAnnuo > 0 ? l.consumoAnnuo / l.consumoPeriodo : (l.giorni > 0 ? 365 / l.giorni : null);
  const annual = factor ? Math.abs(saving) * factor : null;
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3" data-testid={prefix}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-medium text-sm">Analisi bolletta {commodity === "GAS" ? "gas" : "luce"}</p>
        {sim.prova && <Badge variant="outline" data-testid={`${prefix}-test-badge`}>Ambiente di prova</Badge>}
      </div>
      {sim.ok && (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          <Row label="Fornitore attuale" value={l.fornitoreAttuale} testid={`${prefix}-supplier`} />
          <Row label="Periodo" value={l.periodoDal ? `${fmtD(l.periodoDal)} – ${fmtD(l.periodoAl)}` : null} />
          <Row label="Consumo nel periodo" value={l.consumoPeriodo != null ? `${l.consumoPeriodo} ${unit}` : null} />
          <Row label="Consumo annuo" value={l.consumoAnnuo != null ? `${l.consumoAnnuo} ${unit}` : null} testid={`${prefix}-annual`} />
          {commodity === "LUCE" && <Row label="Potenza impegnata" value={l.potenzaImpegnata != null ? `${l.potenzaImpegnata} kW` : null} />}
          <Row label="Totale bolletta" value={eur(l.totalePeriodo)} testid={`${prefix}-total`} />
        </div>
      )}
      {sim.ok && !comparable && <p className="text-sm font-medium text-amber-500" role="status" data-testid={`${prefix}-comparison-unavailable`}>Confronto non disponibile: non è possibile stabilire il risparmio con questi dati.</p>}
      {showCompare && (
        <div className={`rounded-md p-3 text-sm flex items-center gap-3 ${saving === 0 ? "bg-muted border border-border" : saving > 0 ? "bg-teal-500/10 border border-teal-500/40" : "bg-amber-500/10 border border-amber-500/40"}`} data-testid={`${prefix}-compare`}>
          {saving !== 0 && (saving > 0 ? <TrendingDown className="w-5 h-5 text-teal-500" /> : <TrendingUp className="w-5 h-5 text-amber-500" />)}
          <div>
            <p>Oggi paga <strong>{eur(c.totaleAttuale)}</strong> · con <strong>{sim.offerta?.nome}</strong> pagherebbe <strong>{eur(c.totaleOfferta)}</strong></p>
            <p className={saving === 0 ? "text-muted-foreground font-medium" : saving > 0 ? "text-teal-500 font-medium" : "text-amber-500 font-medium"} data-testid={`${prefix}-saving`}>
              {saving === 0 ? "Nessuna differenza di spesa nel periodo." : saving > 0
                ? `Con Venergia avrebbe risparmiato ${eur(saving)}${pct(c.risparmioPercentuale)} ${period}`
                : `Con Venergia avrebbe speso ${eur(Math.abs(saving))} in più${pct(c.risparmioPercentuale)} ${period}`}
            </p>
            {annual > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid={`${prefix}-annual-estimate`}>
                Stima su 12 mesi ({l.consumoAnnuo} {unit}/anno): circa <strong className={saving > 0 ? "text-teal-500" : "text-amber-500"}>{eur(annual)}</strong> {saving > 0 ? "di risparmio" : "di spesa in più"}
              </p>
            )}
          </div>
        </div>
      )}
      {alternatives.length > 0 && (
        <div className="text-xs space-y-1" data-testid={`${prefix}-alternatives`}>
          <p className="text-muted-foreground">Altre offerte confrontate:</p>
          {alternatives.map((a, i) => {
            const amount = savings[a.codice || a.offerta?.codice];
            return <p key={a.codice || i} data-testid={`${prefix}-alternative-${a.codice || i}`}>{a.nome || a.offerta?.nome || a.codice} · {eur(a.totale ?? a.totaleOfferta ?? a.offerta?.totale)} ({amount === 0 ? "stessa spesa" : `${amount > 0 ? "risparmio" : "spesa in più"} ${eur(Math.abs(amount))}`})</p>;
          })}
        </div>
      )}
      <Problems sim={sim} prefix={prefix} />
      {showNote && <p className="text-xs text-muted-foreground" data-testid={`${prefix}-note`}>{sim.nota}</p>}
    </div>
  );
}
